// Runtime platform brand — the platform's OWN identity, fetched once at boot
// from GET /v1/public/platform-brand and applied app-wide. Unset fields fall
// back to the built-in ExamBase defaults, so an un-rebranded platform looks
// exactly as it does today.
//
// This module holds the brand for the imperative, non-React surfaces (document
// title, favicon, theme CSS variables). React components read the same values
// through PlatformBrandProvider's context. Keeping a plain module holder here
// lets usePageTitle (a hook that runs before/around the provider) read the name
// without a context dependency.

export interface PlatformBrand {
  name: string;
  tagline: string;
  footer_text: string | null;
  legal_entity: string;
  company_number: string;
  primary_hsl: string | null;
  accent_hsl: string | null;
  logo_url: string | null;
  icon_url: string | null;
  // Certificate defaults. Reach the certificate PDF only — applyBrand() never
  // reads these, so setting them does NOT re-skin the app the way primary_hsl /
  // logo_url do.
  cert_accent_hsl: string | null;
  cert_logo_url: string | null;
}

/** Raw stored config (null = unset) — the shape the admin editor round-trips. */
export interface PlatformBrandingStored {
  name: string | null;
  tagline: string | null;
  footer_text: string | null;
  legal_entity: string | null;
  company_number: string | null;
  email_from: string | null;
  primary_hsl: string | null;
  accent_hsl: string | null;
  logo_r2_key: string | null;
  icon_r2_key: string | null;
  cert_accent_hsl: string | null;
  cert_logo_r2_key: string | null;
}

export interface PlatformBrandDefaults {
  name: string;
  tagline: string;
  legal_entity: string;
  company_number: string;
}

// Must match apps/api/src/services/platformBranding.ts PLATFORM_BRAND_DEFAULTS.
export const DEFAULT_BRAND: PlatformBrand = {
  name: "ExamBase",
  tagline: "UK Functional Skills assessment platform",
  footer_text: null,
  legal_entity: "Sitenet Tech Ltd",
  company_number: "12750911",
  primary_hsl: null,
  accent_hsl: null,
  logo_url: null,
  icon_url: null,
  cert_accent_hsl: null,
  cert_logo_url: null,
};

let current: PlatformBrand = DEFAULT_BRAND;

export function getBrand(): PlatformBrand {
  return current;
}

export function getBrandName(): string {
  return current.name || DEFAULT_BRAND.name;
}

/**
 * Apply the loaded brand to the imperative surfaces: stash it for getBrand(),
 * point the favicon at a custom icon, and override the theme colour variables.
 * Safe to call on the server-render path guard (checks for document).
 */
export function applyRuntimeBrand(brand: PlatformBrand): void {
  current = brand;
  if (typeof document === "undefined") return;

  // Favicon + apple-touch icon → the custom icon, when set.
  if (brand.icon_url) {
    for (const rel of ["icon", "apple-touch-icon"]) {
      let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", brand.icon_url);
    }
  }

  // Theme colours (shadcn HSL custom properties). Only override when set, and
  // also set a contrasting *-foreground so text on the colour stays legible —
  // otherwise a dark custom colour keeps the theme's dark foreground.
  const root = document.documentElement;
  if (brand.primary_hsl) {
    root.style.setProperty("--primary", brand.primary_hsl);
    const fg = contrastForeground(brand.primary_hsl);
    if (fg) root.style.setProperty("--primary-foreground", fg);
  }
  if (brand.accent_hsl) {
    root.style.setProperty("--accent", brand.accent_hsl);
    const fg = contrastForeground(brand.accent_hsl);
    if (fg) root.style.setProperty("--accent-foreground", fg);
  }
}

// Pick white vs near-black foreground from an "H S% L%" colour's lightness.
// Returns null (leave the theme default) if the value can't be parsed.
function contrastForeground(hsl: string): string | null {
  const m = hsl.match(/^\d{1,3}\s+\d{1,3}%\s+(\d{1,3})%$/);
  if (!m) return null;
  const l = Number(m[1]);
  return l < 55 ? "0 0% 100%" : "0 0% 10%";
}
